/**
 * S4 · Generating (B3 Group 2)
 *
 * Preconditions when this screen mounts: authed session, user-scope biometric +
 * terms consents present (carried by graduate-consumer), consumer_profiles row
 * with selfie_storage_path bound.
 *
 * Steps:
 *  1) attach_selfie_to_user(selfiePath, 1) — writes a user-scope, selfie-bound
 *     biometric consent row (the gate looks up `has_valid_consent_for_selfie`).
 *  2) generate-avatar (authenticated path). numVariants: program config or 4.
 *  3) Poll pollGenerations until all complete or any fail.
 *  4) Navigate to S5 with the generation ids in state.
 *
 * The ring/countdown are cosmetic: they estimate progress against a nominal
 * budget so the wait never looks static, but they never fake completion —
 * `done` only flips once the real request above resolves.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { attachSelfieToUser, countGenerationAttempts, isStaffGenerationExempt, listGenerationsForPick, listInFlightGenerationsForPick, startGeneration, pollGenerations } from "@/services/avatar";
import { readSessionGenerationIds, rememberSessionGenerations, readOrderEpoch } from "@/lib/generation-session";

import { resolveFreeAttempts } from "@/lib/consumer-config";
import { describeGenerationError, type GenerationErrorInfo } from "@/lib/generation-errors";
import { readSelfiePath, readStylePick, recordFunnelEvent } from "@/lib/consumer-session";
import { recordSignal } from "@/lib/funnel-signals";
import { resolveTaxonomyPick } from "@/lib/taxonomy-link";
import { useConsumerFlow } from "../ConsumerFlow";
import { FunnelPage, FunnelHeader, PrimaryButton, GhostButton } from "@/components/consumer/chrome";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S4_SECTION, S4_DEFAULTS } from "@/content/screens/s4";

const RING_R = 88;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;
/**
 * B5 · the arc starts at 12 O'CLOCK and runs CLOCKWISE. Two half-arcs from the
 * top of the circle (sweep-flag 1 is clockwise on screen in SVG's y-down space).
 * It previously started at 3 o'clock, which read as a quarter already elapsed.
 */
const RING_PATH = `M 100 ${100 - RING_R} A ${RING_R} ${RING_R} 0 0 1 100 ${100 + RING_R} A ${RING_R} ${RING_R} 0 0 1 100 ${100 - RING_R}`;

const NOMINAL_SECONDS = 60;

const cmKeyframes = `
@keyframes cmSpin { to { transform: rotate(360deg); } }
@keyframes cmFloat { 0%, 100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-10px) rotate(-3deg); } }
@keyframes cmPop { 0% { opacity: 0; transform: scale(0.84); } 60% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
`;

export default function S4Generating() {
  const { t } = useSiteContent(S4_SECTION, S4_DEFAULTS);
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const location = useLocation();

  const STAGE_LABELS = [
    t("s4.stage.checkingSelfie"),
    t("s4.stage.drawingFace"),
    t("s4.stage.puttingInScene"),
    t("s4.stage.layingOutCard"),
  ];
  const [status, setStatus] = useState<string>(t("s4.status.preparing"));
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<GenerationErrorInfo | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const [slow, setSlow] = useState(false);
  // Visible allowance ("N of 5"). generate-avatar remains the authority; the
  // client pre-flight below just stops the user clicking into the wall.
  const freeAttempts = resolveFreeAttempts((program.config ?? {}) as Record<string, any>);
  const [attemptsUsed, setAttemptsUsed] = useState<number | null>(null);
  // Staff (super_admin/admin) are exempt from the consumer allowance — mirrors
  // the server-side user_roles check in generate-avatar.
  const [staffExempt, setStaffExempt] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await supabase.auth.getUser();
      if (!sess?.user) return;
      // No tenant filter: RLS already scopes this read to the caller's own rows.
      const [used, exempt] = await Promise.all([
        countGenerationAttempts(sess.user.id),
        isStaffGenerationExempt(sess.user.id),
      ]);
      if (cancelled) return;
      setAttemptsUsed(used);
      setStaffExempt(exempt);
    })();
    return () => { cancelled = true; };
  }, [done]);

  const startedRef = useRef(false);

  // Local timer drives the ring/countdown cosmetically; it never triggers
  // navigation or marks generation complete on its own.
  useEffect(() => {
    if (done || error) return;
    const t = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        if (next >= NOMINAL_SECONDS) setSlow(true);
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [done, error]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getUser();
        if (!sess?.user) {
          const info = await describeGenerationError({ status: 401 }, freeAttempts);
          setErrorInfo(info);
          setError(t("s4.errors.notSignedIn"));
          return;
        }

        // ── RESUME GATE (nav dead-end fix) ──
        // Back-then-forward must return the user to the avatars they ALREADY
        // have, never spend another generation. Generations are durable, so if
        // completed rows exist for this exact pick inside the window we hand
        // straight off to the reveal/selection screen. Only an explicit
        // "Regenerate" (state.forceNew from S5) skips this gate.
        const forceNew = (location.state as any)?.forceNew === true;
        const resumePick = readStylePick(program.slug);
        // Throws UnknownGallerySlugError for a slug the programme does not
        // expose — caught by the outer handler below, never defaulted.
        const resumeTriple = resumePick ? resolveTaxonomyPick(resumePick, program.config, program.styles) : null;
        const selfiePath = readSelfiePath(program.slug);
        if (!forceNew && resumeTriple) {
          // A request can outlive this page. Rejoin the newest server-side
          // batch for the exact selfie + taxonomy pick instead of invoking the
          // generator again. This path never inserts rows, so it cannot spend
          // a second allowance unit.
          if (selfiePath) {
            const inFlight = await listInFlightGenerationsForPick(sess.user.id, selfiePath, {
              styleId: resumeTriple.styleId,
              subStyleId: resumeTriple.subStyleId,
              themeId: resumeTriple.themeId,
            });
            const inFlightRows = inFlight.data ?? [];
            if (inFlightRows.some((row) => row.status === "generating")) {
              const ids = inFlightRows.map((row) => row.id);
              rememberSessionGenerations(program.slug, resumeTriple, ids);
              setProgress({
                done: inFlightRows.filter((row) => row.status === "completed").length,
                total: ids.length,
              });
              setStatus(t("s4.status.generating"));
              for (let i = 0; i < 880; i++) {
                const polled = await pollGenerations(ids);
                if (!polled.error) {
                  const rows: any[] = (polled.data as any)?.generations ?? [];
                  const doneCount = rows.filter((row) => row.status === "completed").length;
                  const settled = rows.filter((row) => row.status === "completed" || row.status === "failed").length;
                  setProgress({ done: doneCount, total: ids.length });
                  if (settled >= ids.length) break;
                }
                await new Promise((resolve) => setTimeout(resolve, 750));
              }
              setDone(true);
              navigate("../reveal", {
                replace: true,
                state: { generationIds: ids, resumed: true },
              });
              return;
            }
          }


          const existing = await listGenerationsForPick(sess.user.id, {
            styleId: resumeTriple.styleId,
            subStyleId: resumeTriple.subStyleId,
            themeId: resumeTriple.themeId,
          });
          let rows = (existing.data ?? []).filter((r: any) => r.generated_image_url);
          // SESSION SCOPE: resume only what THIS tab session generated for this
          // pick. If the ledger is empty (fresh tab) we adopt the style+recent
          // rows as this session's set rather than forcing a regeneration.
          const ledger = readSessionGenerationIds(program.slug, resumeTriple);
          if (ledger.length) rows = rows.filter((r: any) => ledger.includes(r.id));
          if (rows.length) {
            rememberSessionGenerations(program.slug, resumeTriple, rows.map((r: any) => r.id));
            setDone(true);
            navigate("../reveal", {
              replace: true,
              state: { generationIds: rows.map((r: any) => r.id), resumed: true },
            });
            return;
          }
        }


        // ── Client pre-flight hard-stop ──
        // Stop before the request when the allowance is spent, so the user gets
        // a clear message instead of a wasted round trip + raw 402. Staff are
        // exempt (same rule the server re-derives from user_roles).
        const [usedNow, exemptNow] = await Promise.all([
          countGenerationAttempts(sess.user.id),
          isStaffGenerationExempt(sess.user.id),
        ]);
        setAttemptsUsed(usedNow);
        setStaffExempt(exemptNow);
        if (!exemptNow && usedNow >= freeAttempts) {
          setLimitReached(true);
          const info = await describeGenerationError({ status: 402 }, freeAttempts);
          setErrorInfo(info);
          setError(`You've used all ${freeAttempts} of your free generations.`);
          return;
        }

        const pick = readStylePick(program.slug);
        if (!selfiePath || !pick?.style_id) {
          setErrorInfo({
            code: "VALIDATION_ERROR",
            status: null,
            message: t("s4.errors.missingSelfieOrStyle"),
            isLimit: false,
            retryable: false,
            recoveryRoute: selfiePath ? "style" : "selfie",
          });
          setError(t("s4.errors.missingSelfieOrStyle"));
          return;
        }

        setStatus(t("s4.status.bindingConsent"));
        const attach = await attachSelfieToUser(selfiePath, 1);
        if ((attach as any).error) throw (attach as any).error;

        setStatus(t("s4.status.requesting"));
        const numVariants = Number(program.config?.numVariants ?? 2);
        // Re-link the gallery pick onto the 3-tier prompt taxonomy so the
        // composed prompt keeps the Cardmoji style (cutout) + sub-style + hobby.
        const triple = resolveTaxonomyPick(pick, program.config, program.styles)!;
        const started = await startGeneration({
          programId: program.program_id,
          styleId: triple.styleId,
          subStyleId: triple.subStyleId,
          themeId: triple.themeId,
          selfieStoragePath: selfiePath,
          numVariants,
          // Lane-2 parity: a reorder is a NEW order epoch, so it must not replay
          // the previous order's completed idempotency entry.
          batchKey: readOrderEpoch(program.slug),
        });

        if (started.error) throw started.error;
        const ids: string[] = (started.data as any)?.generationIds || [];
        if (!ids.length) throw new Error(t("s4.errors.noGenerationsQueued"));
        setProgress({ done: 0, total: ids.length });
        recordFunnelEvent(program.slug, "generating", program.program_id, { variants: ids.length });

        // Poll ~ every 750ms. Hand off to S5 as soon as the FIRST variant is
        // ready — S5 keeps polling the rest and cuts each one out as it lands,
        // so the reveal is progressive instead of blocking on the whole batch.
        setStatus(t("s4.status.generating"));
        let handedOff = false;
        for (let i = 0; i < 120 && !handedOff; i++) {
          // First pass fires IMMEDIATELY: generate-avatar returns only after the
          // model work is done, so the rows are usually already `completed` and
          // an upfront sleep would add pure dead time.
          if (i > 0) await new Promise((r) => setTimeout(r, 750));
          const p = await pollGenerations(ids);

          if (p.error) continue;
          const rows: any[] = (p.data as any)?.generations || (p.data as any) || [];
          const doneCount = rows.filter((r) => r.status === "completed").length;
          const failed = rows.filter((r) => r.status === "failed").length;
          setProgress({ done: doneCount, total: ids.length });
          if (doneCount >= 1 || failed >= ids.length) handedOff = true;
        }

        setDone(true);
        rememberSessionGenerations(program.slug, triple, ids);
        navigate("../reveal", { state: { generationIds: ids } });
      } catch (e: any) {
        // Map typed edge-function failures (402 GENERATION_LIMIT_REACHED etc.)
        // to consumer copy instead of "non-2xx status code".
        const info = await describeGenerationError(e, freeAttempts);
        setErrorInfo(info);
        // A failure here is a drop-off CAUSE, not a step: it explains why the
        // reveal arrival is missing. The typed code goes with it.
        recordSignal("generation_failed", { code: info.code, status: info.status });
        if (info.code !== "UNKNOWN" || info.status !== null) {
          if (info.isLimit) setLimitReached(true);
          setError(info.message);
        } else {
          setError(e?.message || String(e));
        }
      }
    })();
  }, [program.id, program.slug, navigate, retryNonce]);

  const retryGeneration = () => {
    startedRef.current = false;
    setError(null);
    setErrorInfo(null);
    setLimitReached(false);
    setDone(false);
    setSlow(false);
    setElapsed(0);
    setProgress({ done: 0, total: 0 });
    setStatus(t("s4.status.preparing"));
    setRetryNonce((value) => value + 1);
  };

  const frac = done ? 1 : Math.min(elapsed / NOMINAL_SECONDS, slow ? 1 : 0.98);
  const dashOffset = RING_CIRCUMFERENCE * frac;
  const secondsLeft = Math.max(NOMINAL_SECONDS - elapsed, 0);
  const stageCount = STAGE_LABELS.length;

  return (
    <FunnelPage tone="ink" pb="pb-0" className="flex flex-col" testId="s4-generating">
      <style>{cmKeyframes}</style>
      <div className="mx-auto w-full px-4 pt-[18px] md:max-w-[720px] md:px-8 md:pt-7">
        <FunnelHeader backTo="../verify" backLabel="Back" step={t("s4.header.stepLabel")} tone="paper" className="mb-0" />
      </div>

      <div className="mx-auto flex w-full flex-1 flex-col items-center justify-center gap-6 px-4 py-9 md:max-w-[720px] md:px-8 md:py-11">
        <div className="relative aspect-square w-[216px] flex-none">
          <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full">
            {/*
              B5 · the arc is an explicit CLOCKWISE path starting at 12 o'clock
              (sweep-flag 1 in SVG's y-down space = clockwise on screen). A bare
              <circle> left the direction to the renderer, and the earlier path
              started at 3 o'clock, so the countdown looked a quarter gone.
            */}
            <path d={RING_PATH} fill="none" stroke="rgba(247,245,241,0.14)" strokeWidth="8" />
            <path
              d={RING_PATH}
              fill="none"
              stroke={error ? "#EEB42C" : done ? "#20AE36" : slow ? "#EEB42C" : "#FFD400"}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              /* NEGATIVE offset: the gap must GROW clockwise from 12 o'clock.
                 A positive offset empties the arc from the far end, which reads
                 as the ring running counterclockwise. */
              strokeDashoffset={-dashOffset}
              style={{ transition: "stroke-dashoffset 980ms linear" }}
              data-testid="s4-generating-ring"
            />

          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
            <span className="font-brand text-[52px] font-semibold leading-none tracking-[-0.04em] text-ink-foreground" data-testid="s4-generating-countdown">
              {error ? "!" : done ? "✓" : slow ? "···" : secondsLeft}
            </span>
            <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-foreground/60">
              {error ? "stopped" : done ? t("s4.countdown.readyLabel") : slow ? t("s4.countdown.stillWorking") : secondsLeft === 1 ? t("s4.countdown.secondLeft") : t("s4.countdown.secondsLeft")}
            </span>
          </div>
        </div>

        <div className="text-center">
          <h1 className="font-brand mb-2.5 text-[28px] font-semibold leading-[1.05] tracking-[-0.03em] text-balance text-ink-foreground md:text-[clamp(28px,3.8vw,44px)]">
             {limitReached
              ? "You've reached your free limit"
               : error
               ? "We couldn't create your face"
              : done ? t("s4.heading.done") : slow ? t("s4.heading.slow") : t("s4.heading.working")}
          </h1>
          <p className="mx-auto max-w-[34ch] text-[15px] leading-[1.45] text-pretty text-ink-foreground/80" data-testid="s4-generating-status">
             {limitReached
              ? `You've used all ${freeAttempts} of your free generations. You can still finish your card with a face you've already made.`
               : error
               ? error
              : done
                ? t("s4.body.done")
                : slow
                  ? t("s4.body.slow")
                  : status}
          </p>
          {staffExempt ? (
            <p className="mt-2 text-[13px] text-ink-foreground/60" data-testid="s4-attempts">
              Staff account — not metered against the free allowance.
            </p>
          ) : attemptsUsed !== null && (
            <p className="mt-2 text-[13px] text-ink-foreground/60" data-testid="s4-attempts">
              {t("s4.attempts")
                .replace("{used}", String(Math.min(attemptsUsed, freeAttempts)))
                .replace("{total}", String(freeAttempts))}
            </p>
          )}
          {error && (
            <div className="mx-auto mt-5 flex w-full max-w-[340px] flex-col gap-2.5" data-testid="s4-failure-actions">
              {errorInfo?.retryable && (
                <PrimaryButton onClick={retryGeneration} data-testid="s4-retry">Try again</PrimaryButton>
              )}
              {errorInfo?.recoveryRoute === "verify" && (
                <PrimaryButton onClick={() => navigate("../verify")} data-testid="s4-fix-consent">Review consent</PrimaryButton>
              )}
              {errorInfo?.recoveryRoute === "selfie" && (
                <PrimaryButton onClick={() => navigate("../selfie")} data-testid="s4-fix-selfie">Add your selfie</PrimaryButton>
              )}
              {errorInfo?.recoveryRoute === "style" && (
                <PrimaryButton onClick={() => navigate("../style")} data-testid="s4-fix-style">Choose a style</PrimaryButton>
              )}
              {limitReached && (
                <PrimaryButton onClick={() => navigate("../reveal")} data-testid="s4-limit-continue">See your cards</PrimaryButton>
              )}
              <GhostButton onClick={() => navigate("/")} className="border-paper/30 bg-transparent text-paper hover:border-paper" data-testid="s4-home">
                Back to home
              </GhostButton>
            </div>
          )}
        </div>



        {!error && <div className="flex w-full max-w-[340px] flex-col gap-2" data-testid="s4-generating-progress">
          {STAGE_LABELS.map((label, i) => {
            const stageDone = done || frac >= (i + 1) / stageCount;
            const active = !done && !stageDone && frac >= i / stageCount;
            return (
              <div
                key={label}
                className="flex items-center gap-3 rounded-[14px] px-[15px] py-[13px] transition-colors"
                style={{
                  background: stageDone ? "rgba(32,174,54,0.16)" : active ? "rgba(247,245,241,0.14)" : "rgba(247,245,241,0.08)",
                }}
              >
                <span
                  className="grid h-[22px] w-[22px] flex-none place-items-center rounded-full"
                  style={{
                    border: stageDone ? "0" : "2px solid rgba(247,245,241,0.35)",
                    borderTopColor: active ? "#FFD400" : undefined,
                    background: stageDone ? "#20AE36" : "transparent",
                    animation: active ? "cmSpin 900ms linear infinite" : "none",
                  }}
                >
                  {stageDone ? (
                    <svg viewBox="0 0 20 20" className="h-[13px] w-[13px]">
                      <path d="M5.4 10.4l3 3 6.2-6.6" fill="none" stroke="#FFFFFF" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </span>
                <span className="text-[15px] font-medium text-ink-foreground">{label}</span>
              </div>
            );
          })}
        </div>}

        {progress.total > 0 && (
          <div className="sr-only" data-testid="s4-generating-count">
            {progress.done}/{progress.total}
          </div>
        )}

        {done && (
          <div
            className="flex w-full max-w-[340px] items-center justify-center rounded-full bg-volt px-7 py-4 text-[17px] font-semibold text-ink"
            style={{ animation: "cmPop 480ms cubic-bezier(0.22, 1, 0.36, 1) both" }}
          >
            {t("s4.cta.seeCard")}
          </div>
        )}

        <p className="max-w-[38ch] text-center text-[13px] leading-[1.5] text-ink-foreground/60">
          {t("s4.footer.privacyNote")}
        </p>

        {error && <span className="sr-only" data-testid="s4-generating-error">{error}</span>}
      </div>
    </FunnelPage>
  );
}
